import React from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, Container, alpha, Grid } from '@mui/material';
import {
  Warning as ProblemIcon,
  Lightbulb as SolutionIcon,
  TrendingDown as TrendDownIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  Psychology as AIIcon,
  Speed as SpeedIcon,
  AutoAwesome as AutoIcon,
} from '@mui/icons-material';
import { tokens } from '../../theme';

const ProblemSolutionSection: React.FC = () => {
  const problems = [
    {
      icon: <TrendDownIcon />,
      title: 'Data Everywhere',
      description: 'Scattered across Stripe, Shopify, QuickBooks, spreadsheets... impossible to get a clear picture.',
    },
    {
      icon: <TimeIcon />,
      title: 'No Time to Analyze',
      description: 'You\'re running a business, not building dashboards. Who has hours to crunch numbers?',
    },
    {
      icon: <MoneyIcon />,
      title: 'Analysts Are Expensive',
      description: 'Hiring a data analyst starts at $80k/year. Most small businesses can\'t afford that.',
    },
  ];

  const solutions = [
    {
      icon: <AIIcon />,
      title: 'AI Does the Heavy Lifting',
      description: 'Clover and Stripe each pull when you run a sync from their own card. Anomaly detection runs when you generate insights on the dashboard.',
    },
    {
      icon: <SpeedIcon />,
      title: 'No Manual Exports',
      description: 'Connect Clover and Stripe once. Pull fresh numbers whenever you want them, from the sync button on each card on the Integrations page. No CSVs, no spreadsheets, no copying figures into a template.',
    },
    {
      icon: <AutoIcon />,
      title: 'What Changed, Not What To Do',
      description: 'What happened, and which days do not fit the pattern. Not why it happened: root cause analysis is not built.',
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: tokens.background.gradient,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '100%',
          background: `radial-gradient(ellipse at center, ${alpha(tokens.primary.main, 0.03)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Grid container spacing={{ xs: 4, md: 8 }}>
          {/* Problem Column */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0.15, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.6 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    background: `linear-gradient(135deg, ${alpha(tokens.error.main, 0.15)} 0%, ${alpha(tokens.error.main, 0.05)} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: tokens.error.main,
                  }}
                >
                  <ProblemIcon />
                </Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: tokens.error.main,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                  }}
                >
                  The Problem
                </Typography>
              </Box>

              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: tokens.text.primary,
                  mb: 4,
                  letterSpacing: '-0.02em',
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
                }}
              >
                Running blind without{' '}
                <Box component="span" sx={{ color: tokens.error.main }}>
                  real insights
                </Box>
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {problems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 3,
                        p: 3,
                        borderRadius: '16px',
                        background: tokens.background.paper,
                        border: `1px solid ${tokens.border.light}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: alpha(tokens.error.main, 0.3),
                          boxShadow: `0 8px 30px ${alpha(tokens.error.main, 0.08)}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          background: alpha(tokens.error.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: tokens.error.main,
                          flexShrink: 0,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, color: tokens.text.primary, mb: 0.5 }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: tokens.text.secondary, lineHeight: 1.6 }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </motion.div>
          </Grid>

          {/* Solution Column */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0.15, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.6 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    background: tokens.success.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                  }}
                >
                  <SolutionIcon />
                </Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: tokens.success.main,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                  }}
                >
                  The Solution
                </Typography>
              </Box>

              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: tokens.text.primary,
                  mb: 4,
                  letterSpacing: '-0.02em',
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
                }}
              >
                AI-powered clarity{' '}
                <Box
                  component="span"
                  sx={{
                    background: tokens.success.gradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  delivered automatically
                </Box>
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {solutions.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 3,
                        p: 3,
                        borderRadius: '16px',
                        background: tokens.background.paper,
                        border: `1px solid ${alpha(tokens.success.main, 0.2)}`,
                        boxShadow: `0 4px 20px ${alpha(tokens.success.main, 0.05)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateX(8px)',
                          borderColor: alpha(tokens.success.main, 0.4),
                          boxShadow: `0 8px 30px ${alpha(tokens.success.main, 0.1)}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          background: tokens.success.gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          flexShrink: 0,
                          boxShadow: `0 8px 20px ${alpha(tokens.success.main, 0.3)}`,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, color: tokens.text.primary, mb: 0.5 }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: tokens.text.secondary, lineHeight: 1.6 }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProblemSolutionSection;
